import { EntityStoreService } from "./entity-store.service";
import { EntityApiService } from "./entity-api.service";
import { catchError, firstValueFrom, map, race, Subject } from "rxjs";
import { computed, Signal } from "@angular/core";
import {
  EntityMetadata,
  EntityStoreState,
  EntityStoreStateBase,
  Identifiable,
  QueryParam,
} from "../store-types.model";
import { StoreFacadeService } from "../store/store-facade.service";
import { Untrack, UpdateStatus } from "../utils/store-utils.service";
import { ReduxTrack } from "../utils/devtool.service";

/**
 * Service class for managing entities through the store and API.
 *
 * @template TStore - The type of entities managed by the service.
 */
export class EntityFacadeService<
  TEntity extends Identifiable,
  TApi extends EntityApiService<TEntity>,
  TState extends EntityStoreStateBase = EntityStoreState,
  TMetadata extends EntityMetadata = EntityMetadata,
  TStore extends EntityStoreService<
    TEntity,
    TMetadata,
    TState
  > = EntityStoreService<TEntity, TMetadata, TState>,
> extends StoreFacadeService<TState, TStore> {
  static functionName;
  public elements: Signal<Signal<TEntity>[]>;

  /**
   * Constructs a new EntityFacadeService.
   *
   * @param {EntityStoreService<TStore>} store - The entity store service.
   * @param {EntityApiService<TApi>} api - The entity API service.
   */
  constructor(
    protected api: TApi,
    store?: TStore,
  ) {
    super(
      store ??
        (new EntityStoreService<TEntity>(
          EntityStoreService.initialState,
        ) as unknown as TStore),
    );
    this.elements = this.store.entities;
    this.store.initStatus(this);
  }

  /**
   *
   * return Signal entity from the store by ID.
   *
   * @param id - id of the entity
   * @throws {Error} Throws an error if the provided ID is null.
   */
  public getById(id: string): Signal<TEntity> {
    return this.store.getById(id);
  }

  public getBySignalId<TOther>(
    signal: Signal<TOther>,
    accessor?: (e: TOther) => string,
  ): Signal<TEntity | null> {
    return computed(() => {
      const value = signal();
      if (!value) {
        return null;
      }
      if (typeof value === "string") {
        return this.store.getById(value)();
      }
      const id = accessor ? accessor(value) : (value as string);
      if (id) {
        return this.store.getById(id)();
      }
      return null;
    });
  }

  /**
   *
   * return Signal entity array;
   *
   * @throws {Error} Throws an error if the provided ID is null.
   */
  public getAllEntities(): Signal<TEntity[]> {
    return this.store.allEntities;
  }

  /**
   * get Metadatas of entity by ID
   * You have to keep a reference of metadata if getMetadata is call in a computed, du not get value in computed function
   *
   * @public
   * @param {string} entityId - The ID of the entity.
   * @returns {Signal<EntityMetadata>} a Signal of the metadatas.
   * @throws {Error} Throws an error if the entity does not exist.
   */
  public getMetadata(entityId: string): Signal<EntityMetadata> {
    return this.store.getMetadata(entityId);
  }

  public getMetadataBySignalId(
    entityId: Signal<string>,
  ): Signal<EntityMetadata> {
    return computed(() => {
      if (entityId()) {
        return this.store.getMetadata(entityId())();
      } else {
        return null;
      }
    });
  }

  @Untrack
  @UpdateStatus
  async loadById(id: string, query?: QueryParam) {
    const existingSignalEntity = this.store.getById(id);
    const canceler$ = new Subject<void>();
    const loadById$ = this.api.loadOne(id, query).pipe(
      map((entity) => {
        return this.store.upsertOne(entity, {
          error: null,
          loading: false,
          canceler$: null,
        });
      }),
      catchError((e) => {
        if (existingSignalEntity()) {
          this.store.patchState({ error: e });
        }
        throw e;
      }),
    );

    const metaDatas = this.getMetadata(id);
    metaDatas()?.canceler$?.next();
    if (metaDatas()) {
      this.store.updateMetadata(id, {
        loading: true,
        error: null,
        canceler$: canceler$,
      });
    }
    return await firstValueFrom(race([canceler$, loadById$]));
  }

  /**
   * Loads all entities from the associated API and adds them to the store.
   * global loading state is updated.
   *
   * @public
   * @returns {Promise<void>} A promise that resolves when all entities are successfully loaded and added to the store.
   * @throws {Error} Throws an error if there is an issue loading the entities.
   */
  @Untrack
  @ReduxTrack
  @UpdateStatus
  async loadAll(query?: QueryParam): Promise<void> {
    this.store.incrementLoading();
    try {
      this.store.addMany(await firstValueFrom(this.api.loadAll(query)));
      this.store.decrementLoading();
    } catch (e) {
      this.store.decrementLoading();
      this.store.patchState({
        error: e,
      });
      throw e;
    }
  }

  /**
   * Removes an entity by its ID through the API.
   * Metadatas of the entity Signal in store are update if exists. ({removing: boolean, saving: boolean})
   *
   * @public
   * @param {string} id - The ID of the entity to remove.
   * @returns {Promise<void>} A promise resolving when the entity is removed.
   * @throws {Error} Throws an error if there is an issue removing the entity.
   */
  @Untrack
  @ReduxTrack
  @UpdateStatus
  async remove(id: string): Promise<void> {
    const signalEntity = this.store.getById(id);
    if (signalEntity()) {
      this.store.updateMetadata(id, { removing: true, saving: true });
    }
    try {
      await firstValueFrom(this.api.remove(id));
      if (signalEntity()) {
        this.store.removeOne(id);
      }
    } catch (e) {
      if (signalEntity()) {
        this.store.updateMetadata(id, { removing: false, saving: false });
      }
      this.store.patchState({ error: e });
      throw e;
    }
  }

  @Untrack
  @ReduxTrack
  @UpdateStatus
  async removeAll(): Promise<void> {
    this.store.removeAll();
  }

  /**
   * Adds a new entity to the API and add it to store.
   *
   * @public
   * @param {TStore} entity - The entity to add.
   * @returns {Promise<Signal<TStore>>} A promise that resolves to the signal of the added entity.
   * @throws {Error} Throws an error if the entity already has an ID or if there is an issue adding the entity.
   */
  @Untrack
  @ReduxTrack
  @UpdateStatus
  async add(entity: TEntity, query?: QueryParam): Promise<Signal<TEntity>> {
    try {
      return this.store.upsertOne(
        await firstValueFrom(this.api.create(entity, query)),
        {
          saving: false,
          saved: true,
        },
      );
    } catch (e) {
      this.store.patchState({ error: e });
      throw e;
    }
  }

  @Untrack
  @ReduxTrack
  @UpdateStatus
  async upsert(entity: TEntity): Promise<Signal<TEntity>> {
    if (entity.id) {
      return this.update(entity);
    } else {
      return this.add(entity);
    }
  }

  /**
   * updates an existing entity in the store and the associated API.
   * Metadatas of the entity Signal in store are update if exists. ({saving: boolean, saved: boolean})
   *
   * @public
   * @param {TStore} entity - The entity to update.
   * @returns {Promise<Signal<TStore>>} A promise that resolves to the signal of the updated entity.
   * @throws {Error} Throws an error if there is an issue updating the entity.
   */
  @Untrack
  @ReduxTrack
  @UpdateStatus
  async update(
    entity: TEntity,
    apiMethod = this.api.update,
  ): Promise<Signal<TEntity>> {
    const signalEntity = this.store.getById(entity.id);
    if (signalEntity()) {
      this.store.updateMetadata(entity.id, { saving: true });
    }
    try {
      return this.store.upsertOne(
        await firstValueFrom(apiMethod.bind(this.api)(entity)),
        {
          saving: false,
          saved: true,
        },
      );
    } catch (e) {
      if (signalEntity()) {
        this.store.updateMetadata(entity.id, { saving: false, saved: false });
      }
      this.store.patchState({ error: e });
      throw e;
    }
  }
}
